import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import classNames from 'classnames';

import FooterContainer from '../FooterContainer/FooterContainer';
import { StaticPage } from '../PageBuilder/PageBuilder';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import CategoryBar from './CategoryBar';
import ProductGrid from './ProductGrid';
import SizeBar from './SizeBar';
import SoldItemsSection from './SoldItemsSection';
import buttonCss from '../LandingPage/components/HomepageButtons.module.css';

import { NamedLink } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getMatchingShoeSizes } from '../../util/shoe-size-converter';
import { parse, stringify } from '../../util/urlHelpers';
import { loadData } from '../SearchPage/SearchPage.duck';
import { fetchSoldItems } from './ResalePage.duck';
import useRevealOnReady from '../../hooks/useRevealOnReady';
import revealCss from '../../styles/contentReveal.module.css';

import css from './ResalePage.module.css';

const RESALE_ROUTE_NAME = 'ResalePage';
const RESALE_LISTING_TYPE = 'sell-used-products';
const DEFAULT_CATEGORY = 'all';
const DEFAULT_RESALE_SORT = 'createdAt';
const RESALE_MIN_STOCK = 1;
const RESALE_STOCK_MODE = 'match-undefined';
const DRAFT_LISTING_ID = '00000000-0000-0000-0000-000000000000';
const LOAD_MORE_PAGE_SIZE = 24;

const isSyncHiddenListing = listing => listing?.attributes?.publicData?._devSyncHidden === true;

// Size filter value mappings - EXACT values from publicData.clothingSize
const CLOTHING_SIZE_OPTIONS = [
  { label: 'ALL SIZES', value: '' }, // clears size filter
  { label: 'XXS / 00 / FR 32', value: 'XXS,US00,FR32' }, // Future use
  { label: 'XS / 0 / 2 / FR 34', value: 'XS,US0,US2,FR34' },
  { label: 'S / 4 / 6 / FR 36', value: 'S,US4,US6,FR36' },
  { label: 'M / 8 / 10 / FR 38', value: 'M,US8,US10,FR38' },
  { label: 'L / 12 / FR 40', value: 'L,US12,FR40' },
  { label: 'XL / 14 / FR 42', value: 'XL,US14,FR42' },
  { label: 'XXL / 16 / FR 44 / FR 46', value: 'XXL,US16,FR44,FR46' },
  { label: 'ONE SIZE', value: 'One Size' },
];

const SHOE_SIZE_OPTIONS = [
  { label: 'ALL SIZES', value: '' },
  { label: '5', value: '5' },
  { label: '5.5', value: '5_5' },
  { label: '6', value: '6' },
  { label: '6.5', value: '6_5' },
  { label: '7', value: '7' },
  { label: '7.5', value: '7_5' },
  { label: '8', value: '8' },
  { label: '8.5', value: '8_5' },
  { label: '9', value: '9' },
  { label: '9.5', value: '9_5' },
  { label: '10', value: '10' },
  { label: '10.5', value: '10_5' },
  { label: '11+', value: '11+' },
];

const parseSearchParams = search =>
  parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

// memoize selector to prevent unnecessary re-renders
const selectListingsAndPagination = createSelector(
  state => state.SearchPage,
  state => state,
  (searchPage, state) => {
    const {
      currentPageResultIds,
      searchInProgress: inProgress,
      pagination,
      searchParams,
      searchListingsError,
    } = searchPage || {};
    return {
      listings: getListingsById(state, currentPageResultIds || []),
      searchInProgress: inProgress,
      pagination,
      searchParams,
      searchListingsError,
    };
  }
);
const selectSoldItems = createSelector(
  state => state.ResalePage,
  resalePage => {
    const {
      soldItems = [],
      fetchSoldItemsInProgress = false,
      fetchSoldItemsResolved = false,
      fetchSoldItemsError = null,
    } = resalePage || {};
    return {
      soldItems,
      fetchSoldItemsInProgress,
      fetchSoldItemsResolved,
      fetchSoldItemsError,
    };
  }
);

const ResalePageComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();
  const config = useConfiguration();
  const routes = useRouteConfiguration();

  const queryParams = useMemo(() => parseSearchParams(location.search), [location.search]);
  const { perPage: _ignoredPerPage, ...queryParamsWithoutPerPage } = queryParams;
  const currentPage = Math.max(1, parseInt(queryParamsWithoutPerPage.page, 10) || 1);
  const productGridRef = useRef(null);
  const [accumulatedListings, setAccumulatedListings] = useState([]);
  const previousBaseQueryKeyRef = useRef(null);
  const previousPageRef = useRef(currentPage);

  const { listings, searchInProgress, pagination, searchParams, searchListingsError } = useSelector(
    selectListingsAndPagination
  );

  const {
    soldItems,
    fetchSoldItemsInProgress,
    fetchSoldItemsResolved,
    fetchSoldItemsError,
  } = useSelector(selectSoldItems);

  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

  const activeCategory = queryParams.pub_categoryLevel1 || DEFAULT_CATEGORY;

  const activeSize =
    activeCategory === 'clothing' ? queryParams.pub_ClothingSize : queryParams.pub_ShoeSize;

  const normalizedQueryParams = useMemo(
    () => ({
      ...queryParamsWithoutPerPage,
      page: currentPage,
      pub_listingType: RESALE_LISTING_TYPE,
      pub_categoryLevel1: queryParamsWithoutPerPage.pub_categoryLevel1 || DEFAULT_CATEGORY,
      sort: DEFAULT_RESALE_SORT,
      minStock: RESALE_MIN_STOCK,
      stockMode: RESALE_STOCK_MODE,
    }),
    [queryParamsWithoutPerPage, currentPage]
  );
  const baseQueryParams = useMemo(() => {
    const { page, ...rest } = normalizedQueryParams;
    return rest;
  }, [normalizedQueryParams]);
  const baseQueryKey = useMemo(() => stringify(baseQueryParams), [baseQueryParams]);

  const expectedQueryKey = useMemo(() => stringify(normalizedQueryParams), [normalizedQueryParams]);
  const activeSearchQueryKey = useMemo(() => {
    const relevantSearchParams = Object.keys(normalizedQueryParams).reduce((collected, key) => {
      const value = searchParams?.[key];

      if (typeof value !== 'undefined') {
        collected[key] = value;
      }

      return collected;
    }, {});

    return stringify(relevantSearchParams);
  }, [normalizedQueryParams, searchParams]);
  const hasResolvedCurrentSearch =
    activeSearchQueryKey === expectedQueryKey &&
    (listings.length > 0 || pagination !== null || Boolean(searchListingsError));
  const hasResolvedSoldItems =
    soldItems.length > 0 || fetchSoldItemsResolved || Boolean(fetchSoldItemsError);
  const hasRevealedContent = useRevealOnReady(hasResolvedCurrentSearch && hasResolvedSoldItems);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  const navigateWithParams = useCallback(
    (params, state) => {
      if (!routes || routes.length === 0) {
        return;
      }

      history.push(createResourceLocatorString(RESALE_ROUTE_NAME, routes, {}, params), state);
    },
    [history, routes]
  );

  useEffect(() => {
    if (searchInProgress) {
      return;
    }

    const didBaseQueryChange = previousBaseQueryKeyRef.current !== baseQueryKey;
    const didPageGoBack = currentPage < previousPageRef.current;

    if (didBaseQueryChange || currentPage === 1 || didPageGoBack) {
      previousBaseQueryKeyRef.current = baseQueryKey;
      previousPageRef.current = currentPage;
      setAccumulatedListings(listings.filter(listing => !isSyncHiddenListing(listing)));
      return;
    }

    previousBaseQueryKeyRef.current = baseQueryKey;
    previousPageRef.current = currentPage;

    if (listings.length === 0) {
      return;
    }

    setAccumulatedListings(prev => {
      const existingIds = new Set(prev.map(listing => listing?.id?.uuid || listing?.id));
      const newListings = listings.filter(listing => {
        if (isSyncHiddenListing(listing)) {
          return false;
        }

        const id = listing?.id?.uuid || listing?.id;
        if (!id || existingIds.has(id)) {
          return false;
        }

        existingIds.add(id);
        return true;
      });

      return newListings.length > 0 ? [...prev, ...newListings] : prev;
    });
  }, [baseQueryKey, currentPage, listings, searchInProgress]);

  const ensureRequiredParams = useCallback(() => {
    const params = { ...queryParams };
    let hasChanged = false;

    if (params.perPage) {
      delete params.perPage;
      hasChanged = true;
    }

    const parsedPage = Number(params.page);
    if (!params.page || Number.isNaN(parsedPage) || parsedPage < 1) {
      params.page = 1;
      hasChanged = true;
    }

    if (params.pub_listingType !== RESALE_LISTING_TYPE) {
      params.pub_listingType = RESALE_LISTING_TYPE;
      hasChanged = true;
    }

    if (!params.pub_categoryLevel1) {
      params.pub_categoryLevel1 = DEFAULT_CATEGORY;
      hasChanged = true;
    }

    if (params.sort !== DEFAULT_RESALE_SORT) {
      params.sort = DEFAULT_RESALE_SORT;
      hasChanged = true;
    }

    const parsedMinStock = Number(params.minStock);
    if (parsedMinStock !== RESALE_MIN_STOCK) {
      params.minStock = RESALE_MIN_STOCK;
      hasChanged = true;
    }

    if (params.stockMode !== RESALE_STOCK_MODE) {
      params.stockMode = RESALE_STOCK_MODE;
      hasChanged = true;
    }

    if (hasChanged) {
      if (routes && routes.length > 0) {
        history.replace(createResourceLocatorString(RESALE_ROUTE_NAME, routes, {}, params));
      }
      return false;
    }

    return true;
  }, [queryParams, history, routes]);

  useEffect(() => {
    if (!config || !routes || routes.length === 0) {
      return;
    }

    const paramsAreValid = ensureRequiredParams();
    if (!paramsAreValid) {
      return;
    }

    dispatch(loadData({}, location.search, config));
  }, [config, routes, location.search, ensureRequiredParams, dispatch]);

  useEffect(() => {
    // Fetch sold items on component mount
    dispatch(fetchSoldItems());
  }, [dispatch]);

  const handleCategoryClick = useCallback(
    category => {
      if (category === activeCategory) return;

      const params = {
        ...queryParams,
        pub_categoryLevel1: category,
        pub_listingType: RESALE_LISTING_TYPE,
      };

      // Clear size filters when switching away from their applicable categories
      if (category !== 'clothing' && params.pub_ClothingSize) delete params.pub_ClothingSize;
      if (category !== 'shoes' && params.pub_ShoeSize) delete params.pub_ShoeSize;

      // Clear page param when changing category
      if (params.page) delete params.page;
      if (params.perPage) delete params.perPage;

      navigateWithParams(params);
    },
    [navigateWithParams, queryParams, activeCategory]
  );

  const handleSizeSelect = useCallback(
    size => {
      const paramKey = activeCategory === 'clothing' ? 'pub_ClothingSize' : 'pub_ShoeSize';
      const newSize = activeSize === size ? null : size;

      const params = { ...queryParams, pub_listingType: RESALE_LISTING_TYPE };

      if (newSize) {
        params[paramKey] = paramKey === 'pub_ShoeSize' ? getMatchingShoeSizes(newSize) : newSize;
      } else {
        delete params[paramKey];
      }

      // Clear page param when changing size
      if (params.page) delete params.page;
      if (params.perPage) delete params.perPage;

      navigateWithParams(params);
    },
    [activeSize, activeCategory, queryParams, navigateWithParams]
  );

  const handleLoadMore = useCallback(() => {
    const nextParams = {
      ...queryParamsWithoutPerPage,
      page: currentPage + 1,
    };

    navigateWithParams(nextParams, { preserveScrollPosition: true });
  }, [navigateWithParams, queryParamsWithoutPerPage, currentPage]);

  // Check if there are more pages to load
  const hasMorePages = pagination && currentPage < pagination.totalPages;

  const shouldShowSizeBar = activeCategory === 'clothing' || activeCategory === 'shoes';
  const sizeOptions = activeCategory === 'clothing' ? CLOTHING_SIZE_OPTIONS : SHOE_SIZE_OPTIONS;

  const schemaTitle = 'Resale - Unvarnished';
  const schemaDescription =
    'Buy and sell pre-loved fashion with confidence through real photo reviews';

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <TopbarContainer />
      <div className={revealedContentClassName}>
        <div className={css.container}>
          <div className={css.titleContainer}>
            <h1 className={css.pageTitle}>Shop now</h1>
            <NamedLink
              name={isAuthenticated ? 'EditListingPage' : 'StartSellingSignupPage'}
              params={
                isAuthenticated
                  ? { slug: 'draft', id: DRAFT_LISTING_ID, type: 'new', tab: 'details' }
                  : undefined
              }
              className={`${css.sellItemButton} ${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
            >
              <ArrowUpRight
                aria-hidden="true"
                className={`${css.sellItemIcon} ${buttonCss.icon}`}
                size={16}
                strokeWidth={2}
                absoluteStrokeWidth
              />
              <span className={css.sellItemText}>SELL AN ITEM</span>
            </NamedLink>
          </div>
          <CategoryBar activeCategory={activeCategory} onCategoryChange={handleCategoryClick} />
          {shouldShowSizeBar && (
            <SizeBar
              sizeOptions={sizeOptions}
              activeCategory={activeCategory}
              activeSize={activeSize}
              onSizeChange={handleSizeSelect}
            />
          )}
          <ProductGrid
            productGridRef={productGridRef}
            searchInProgress={searchInProgress}
            listings={accumulatedListings}
          />

          {hasMorePages && accumulatedListings.length > 0 && !searchInProgress && (
            <section className={css.loadMoreSection}>
              <button
                className={`${css.loadMoreButton} ${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
                onClick={handleLoadMore}
                type="button"
              >
                <ArrowDown
                  className={`${css.loadMoreIcon} ${buttonCss.icon}`}
                  aria-hidden="true"
                  size={16}
                  strokeWidth={2}
                  absoluteStrokeWidth
                />
                <span>LOAD MORE</span>
              </button>
            </section>
          )}

          {searchInProgress && currentPage > 1 && (
            <section className={css.loadMoreSection}>
              <div className={css.productLoading}>Loading more listings...</div>
            </section>
          )}

          <SoldItemsSection
            fetchSoldItemsInProgress={fetchSoldItemsInProgress}
            soldItems={soldItems}
          />
        </div>

        <FooterContainer />
      </div>
    </StaticPage>
  );
};

export default ResalePageComponent;
